// One bill of a settled month up close, inline under the Billing page's bill
// tiles (as MeterPanel sits under the meter tiles on Home): what the home used
// and what it cost, line by line as the PDF prints it with corrections to
// earlier months, the meter readings it was measured between, and the home's
// use per m² against the association's. The mobile app spreads this over two
// screens (the month's bill, one bill's use); the web keeps it on the page.

import type { ReactElement } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { BillingCategory, BillingLine, BillingSettlement } from '../lib/api';
import {
  categoryUsage,
  dayRange,
  formatDay,
  formatMoney,
  formatQuantity,
  formatRate,
  monthChip,
  monthTitle,
  usePerM2,
} from '../lib/billing';
import { KindBubble, useKindTint, type BillKind } from './BillingParts';

export default function BillPanel({
  s,
  c,
  title,
  kind,
  areaM2,
}: {
  s: BillingSettlement;
  c: BillingCategory;
  /** The period's name: "August 2026", the pre-settlement or the year to date. */
  title: string;
  kind: BillKind;
  /** The home's floor area (Settlement's unit), for the per-m² comparison. */
  areaM2?: number;
}): ReactElement {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  return (
    <>
      <div className="page-head">
        <KindBubble kind={kind} />
        <div className="titles">
          <h2>{c.name}</h2>
          <div className="subtitle">{`${title} · ${dayRange(s.from, s.to, lang)}`}</div>
        </div>
      </div>
      <div className="bill-detail">
        <CostCard s={s} c={c} month={title} kind={kind} />
        <MetersCard s={s} c={c} />
        <CompareCard s={s} c={c} kind={kind} areaM2={areaM2} />
      </div>
    </>
  );
}

// The bill's own measured use in its colour — or, for a bill that is only a
// share or a fixed charge, what it came to and why there is no use to show —
// then its lines as quantity × price → amount, its total, and the corrections
// it carries for earlier months.
function CostCard({
  s,
  c,
  month,
  kind,
}: {
  s: BillingSettlement;
  c: BillingCategory;
  month: string;
  kind: BillKind;
}): ReactElement {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { ink } = useKindTint(kind);
  const usage = categoryUsage(c);
  const lines = c.lines.filter((l) => l.kind !== 'correction');
  const corrections = c.lines.filter((l) => l.kind === 'correction');
  const allFee = c.lines.every((l) => l.fee || l.kind === 'correction');

  return (
    <section className="card">
      <div className="bill-label">
        {usage ? t('billing.usageIn', { month }) : t('billing.costFor', { month })}
      </div>
      <div className="bill-big" style={{ color: ink }}>
        {usage ? formatQuantity(usage.quantity, usage.unit) : formatMoney(c.amount, s.currency)}
      </div>
      {!usage && <p className="bill-note">{allFee ? t('billing.fixedCharge') : t('billing.sharedNote')}</p>}

      <div className="bill-rows">
        {lines.map((l, i) => (
          <div key={i} className="bill-row">
            <div className="bill-row-text">
              <span>{l.estimated ? `${l.name} (${t('billing.estimated')})` : l.name}</span>
              {lineBasis(l, s, t) && <span className="bill-row-note">{lineBasis(l, s, t)}</span>}
            </div>
            <span className="bill-row-value">
              {s.money ? formatMoney(l.amount, s.currency) : formatQuantity(l.quantity, l.unit)}
            </span>
          </div>
        ))}
        {corrections.length > 0 && <div className="bill-rows-title">{t('billing.corrections')}</div>}
        {corrections.map((l, i) => (
          <div key={`c${i}`} className="bill-row">
            <span className="bill-row-text">{correctionText(l, lang, t)}</span>
            <span className="bill-row-value">{formatMoney(l.amount, s.currency)}</span>
          </div>
        ))}
        {/* Settlement's bill amount counts its corrections too. */}
        {s.money && (
          <div className="bill-row total">
            <span>{t('billing.totalOf', { name: c.name.toLowerCase() })}</span>
            <span className="bill-row-value">{formatMoney(c.amount, s.currency)}</span>
          </div>
        )}
      </div>
    </section>
  );
}

// The home's meters of this bill's kind, read at the edges of the user's days.
function MetersCard({ s, c }: { s: BillingSettlement; c: BillingCategory }): ReactElement | null {
  const { t } = useTranslation();
  const meters = s.meters.filter((m) => m.kind && m.kind === c.kind);
  if (meters.length === 0 || c.kind === 'other' || c.kind === 'fee') return null;
  return (
    <section className="card">
      <div className="card-title">{t('billing.meters')}</div>
      {meters.map((m, i) => (
        <div key={`${m.name}-${i}`} className="bill-rows">
          <div className="bill-row total">
            <span>{m.name}</span>
            <span className="bill-row-value">{formatQuantity(m.usage, m.unit)}</span>
          </div>
          {m.serial && (
            <div className="bill-row">
              <span className="bill-row-text muted">{t('billing.meterNo')}</span>
              <span className="bill-row-value">{m.serial}</span>
            </div>
          )}
          <div className="bill-row">
            <span className="bill-row-text muted">{t('billing.readingOn', { date: formatDay(m.opening_date) })}</span>
            <span className="bill-row-value">{formatQuantity(m.opening, m.unit)}</span>
          </div>
          <div className="bill-row">
            <span className="bill-row-text muted">{t('billing.readingOn', { date: formatDay(m.closing_date) })}</span>
            <span className="bill-row-value">{formatQuantity(m.closing, m.unit)}</span>
          </div>
        </div>
      ))}
    </section>
  );
}

// The home's use per m² against the association's average that month, as two
// bars on one scale.
function CompareCard({
  s,
  c,
  kind,
  areaM2,
}: {
  s: BillingSettlement;
  c: BillingCategory;
  kind: BillKind;
  areaM2?: number;
}): ReactElement | null {
  const { t } = useTranslation();
  const { ink } = useKindTint(kind);
  const cmp = usePerM2(s, c.code, areaM2);
  if (!cmp) return null;
  const max = Math.max(cmp.own, cmp.average);
  const pct = Math.round(Math.abs(cmp.ratio) * 100);
  const verdict =
    pct === 0
      ? t('billing.aboutAverage')
      : cmp.ratio > 0
        ? t('billing.higherThanAverage', { pct })
        : t('billing.lowerThanAverage', { pct });
  const decimals = cmp.unit === 'kWh' ? 1 : 3;
  const unit = `${cmp.unit}/m²`;
  return (
    <section className="card">
      <div className="card-title">{t('billing.compareTitle')}</div>
      <p className="bill-note">{t('billing.comparePerM2')}</p>
      <Bar label={t('billing.yourHome')} value={`${cmp.own.toFixed(decimals)} ${unit}`} ratio={cmp.own / max} color={ink} />
      <Bar
        label={t('billing.associationAverage')}
        value={`${cmp.average.toFixed(decimals)} ${unit}`}
        ratio={cmp.average / max}
        color="var(--text-muted)"
      />
      <div className="bill-row total">
        <span>{verdict}</span>
        <span className="bill-note">{t('billing.area', { area: formatQuantity(areaM2) })}</span>
      </div>
    </section>
  );
}

function Bar({ label, value, ratio, color }: { label: string; value: string; ratio: number; color: string }): ReactElement {
  return (
    <div className="bill-compare">
      <div className="bill-row">
        <span className="bill-row-text muted">{label}</span>
        <span className="bill-row-value">{value}</span>
      </div>
      <div className="bill-track">
        <span style={{ width: `${Math.max(2, ratio * 100)}%`, background: color }} />
      </div>
    </div>
  );
}

// What a line's amount is made of, as the bill prints it: "11.4 m³ × 42.80
// NOK/m³", "18 units × 2.9883 NOK", with the VAT where the bill shows it.
function lineBasis(l: BillingLine, s: BillingSettlement, t: TFunction): string | null {
  const parts: string[] = [];
  if (l.quantity_kind === 'usage' && l.quantity != null) {
    parts.push(
      s.money && l.rate != null
        ? `${formatQuantity(l.quantity, l.unit)} × ${formatRate(l.rate, s.currency, l.unit)}`
        : formatQuantity(l.quantity, l.unit),
    );
  } else if (l.quantity_kind === 'count' && l.quantity != null) {
    parts.push(
      s.money && l.rate != null
        ? `${formatQuantity(l.quantity)} × ${formatRate(l.rate, s.currency)}`
        : formatQuantity(l.quantity),
    );
  }
  if (s.money && l.vat_shown && l.vat_rate != null) parts.push(t('billing.vatRate', { rate: l.vat_rate }));
  return parts.length > 0 ? parts.join(' · ') : null;
}

// "Correction for January 2026" / "Change of owner 15.06.2026, June–August 2026".
function correctionText(l: BillingLine, lang: string, t: TFunction): string {
  const months = l.corrects?.months ?? [];
  let span = '';
  if (months.length === 1) span = monthTitle(months[0], lang);
  else if (months.length > 1) {
    const first = monthChip(months[0], lang);
    span = `${first.month} – ${monthTitle(months[months.length - 1], lang)}`;
  }
  if (l.corrects?.reason === 'handover') {
    return t('billing.handoverFor', { date: formatDay(l.corrects.handover_date), months: span });
  }
  return t('billing.correctionFor', { months: span });
}
